/**
 * Passport and ID-card machine-readable zone (MRZ) parsing, by code
 * (ICAO 9303). Passports are read in the browser and never sent to the AI.
 * TD3 = passports (2 lines × 44), TD1 = ID cards (3 lines × 30).
 */

export interface MrzResult {
    format: "TD3" | "TD1";
    documentType: string;
    issuingCountry: string;
    documentNumber: string;
    surname: string;
    givenNames: string;
    nationality: string;
    birthDate: string | null;
    sex: "M" | "F" | null;
    expiryDate: string | null;
    /** Every check digit matched. */
    isValid: boolean;
}

const WEIGHTS = [7, 3, 1];
const charValue = (ch: string): number => {
    if (ch === "<") return 0;
    if (/[0-9]/.test(ch)) return Number(ch);
    if (/[A-Z]/.test(ch)) return ch.charCodeAt(0) - 55;
    return -1;
};

/** The ICAO check digit of a field. */
export const checkDigit = (field: string): number => {
    let sum = 0;
    for (let i = 0; i < field.length; i++) {
        const value = charValue(field[i]);
        if (value < 0) return -1;
        sum += value * WEIGHTS[i % 3];
    }
    return sum % 10;
};

const digitOk = (field: string, digit: string) => /[0-9]/.test(digit) && checkDigit(field) === Number(digit);

/**
 * YYMMDD → YYYY-MM-DD. Expiry dates are always in this century or later;
 * birth dates fall back a century when they'd be in the future.
 */
const mrzDate = (yymmdd: string, kind: "birth" | "expiry"): string | null => {
    if (!/^\d{6}$/.test(yymmdd)) return null;
    const yy = Number(yymmdd.slice(0, 2));
    const mm = yymmdd.slice(2, 4);
    const dd = yymmdd.slice(4, 6);
    if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return null;
    const nowYY = new Date().getFullYear() % 100;
    const century = kind === "expiry" ? 2000 : yy > nowYY ? 1900 : 2000;
    return `${century + yy}-${mm}-${dd}`;
};

const names = (field: string) => {
    const [surname, given = ""] = field.split("<<");
    return {
        surname: surname.replace(/</g, " ").trim(),
        givenNames: given.replace(/</g, " ").replace(/\s+/g, " ").trim(),
    };
};

/** OCR text → candidate MRZ lines (only A–Z, 0–9 and <, common OCR slips fixed). */
export const mrzLines = (text: string): string[] =>
    text
        .toUpperCase()
        .split(/\r?\n/)
        .map((line) => line.replace(/\s+/g, "").replace(/«/g, "<").replace(/[^A-Z0-9<]/g, ""))
        .filter((line) => line.length >= 28 && (line.match(/</g) ?? []).length >= 2);

/**
 * Parse the MRZ out of OCR text.
 * @param text - Text read from a passport or ID photo.
 * @returns The fields, or null when no MRZ is found.
 */
export const parseMrz = (text: string): MrzResult | null => {
    const lines = mrzLines(text);
    // TD3: two lines of 44, the first starting with P.
    for (let i = 0; i < lines.length - 1; i++) {
        const l1 = lines[i].padEnd(44, "<").slice(0, 44);
        const l2 = lines[i + 1].padEnd(44, "<").slice(0, 44);
        if (!/^P/.test(l1) || lines[i + 1].length < 40) continue;
        const number = l2.slice(0, 9);
        const birth = l2.slice(13, 19);
        const expiry = l2.slice(21, 27);
        const personal = l2.slice(28, 42);
        const composite = l2.slice(0, 10) + l2.slice(13, 20) + l2.slice(21, 43);
        const isValid = digitOk(number, l2[9]) && digitOk(birth, l2[19]) && digitOk(expiry, l2[27])
            && (personal.replace(/</g, "") === "" ? ["<", "0"].includes(l2[42]) : digitOk(personal, l2[42]))
            && digitOk(composite, l2[43]);
        const sex = l2[20] === "M" || l2[20] === "F" ? l2[20] : null;
        return {
            format: "TD3",
            documentType: l1.slice(0, 2).replace(/</g, ""),
            issuingCountry: l1.slice(2, 5).replace(/</g, ""),
            ...names(l1.slice(5)),
            documentNumber: number.replace(/</g, ""),
            nationality: l2.slice(10, 13).replace(/</g, ""),
            birthDate: mrzDate(birth, "birth"),
            sex,
            expiryDate: mrzDate(expiry, "expiry"),
            isValid,
        };
    }
    // TD1: three lines of 30.
    for (let i = 0; i < lines.length - 2; i++) {
        const [l1, l2, l3] = [lines[i], lines[i + 1], lines[i + 2]].map((line) => line.padEnd(30, "<").slice(0, 30));
        if (!/^[AIC]/.test(l1)) continue;
        const number = l1.slice(5, 14);
        const birth = l2.slice(0, 6);
        const expiry = l2.slice(8, 14);
        const composite = l1.slice(5, 30) + l2.slice(0, 7) + l2.slice(8, 15) + l2.slice(18, 29);
        const isValid = digitOk(number, l1[14]) && digitOk(birth, l2[6]) && digitOk(expiry, l2[14]) && digitOk(composite, l2[29]);
        const sex = l2[7] === "M" || l2[7] === "F" ? l2[7] : null;
        return {
            format: "TD1",
            documentType: l1.slice(0, 2).replace(/</g, ""),
            issuingCountry: l1.slice(2, 5).replace(/</g, ""),
            ...names(l3),
            documentNumber: number.replace(/</g, ""),
            nationality: l2.slice(15, 18).replace(/</g, ""),
            birthDate: mrzDate(birth, "birth"),
            sex,
            expiryDate: mrzDate(expiry, "expiry"),
            isValid,
        };
    }
    return null;
};

/**
 * Read the MRZ from a passport or ID photo, in the browser (OCR loads only
 * when used). The image never leaves the device.
 * @param file - The photo.
 */
export const readMrzFromImage = async (file: File): Promise<MrzResult | null> => {
    const { createWorker } = await import("tesseract.js");
    const worker = await createWorker("eng");
    try {
        await worker.setParameters({ tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<" });
        const { data } = await worker.recognize(file);
        return parseMrz(data.text ?? "");
    } finally {
        await worker.terminate();
    }
};
